import { Alert, Button, Card, Input, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { fileService } from "@/api/services/fileService";

export interface AdvertisementFormContent {
    fileId?: number;
    advertId?: number;
    linkType?: number;
    link?: string;
    activityCode?: string;
    sort?: number;
    [key: string]: unknown;
}

export interface AdvertisementFormValues {
    positionType?: number;
    position?: number;
    displayForm: number;
    timeInterval?: number;
    beginTime?: string;
    endTime?: string;
    content: AdvertisementFormContent[];
}

export type AdvertisementFormPayload = Omit<AdvertisementFormValues, "content"> & {
    content?: AdvertisementFormContent[];
};

export interface AdvertisementFormPageProps {
    id?: number;
    loadDetail?: (id: number) => Promise<AdvertisementFormValues & { id?: number }>;
    update?: (id: number, values: AdvertisementFormPayload) => Promise<unknown>;
}

interface AdvertisementUploadedFile {
    id: number;
    path?: string;
}

/** 复用 Vue PC 广告位详情接口，保持编辑页的原始数据来源。 */
export async function loadAdvertisementDetail(id: number): Promise<AdvertisementFormValues & { id?: number }> {
    const response = await request.service.get<AdvertisementFormValues & { id?: number }>(
        `/dataService/admin/advertisement-position/${id}`
    );
    return response.data;
}

/** 复用 Vue PC 广告位更新接口，并将上传后的图片 ID 与访问地址回填到保存 payload。 */
export async function updateAdvertisement(id: number, values: AdvertisementFormPayload): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/advertisement-position/${id}`, values);
    return response.data;
}

/** 复用 Vue adImg 的上传契约，使用 FormData.file 获取文件 ID 和访问路径。 */
export async function uploadAdvertisementImage(file: Blob): Promise<AdvertisementUploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const uploadedFile = await fileService.uploadFile(formData);
    return { id: Number(uploadedFile.id), path: uploadedFile.path };
}

const initialValues: AdvertisementFormValues = {
    displayForm: 1,
    timeInterval: 1,
    beginTime: "",
    endTime: "",
    content: []
};

function dateOnly(value?: string): string {
    return value?.slice(0, 10) || "";
}

/** 将日期控件值转换成 Vue 接口约定的起止时间。 */
function buildDateTime(value: string | undefined, endOfDay: boolean): string | undefined {
    const date = dateOnly(value);
    if (!date) return undefined;
    return `${date} ${endOfDay ? "23:59:59" : "00:00:00"}`;
}

/** 对齐 Vue ad/edit.vue：特定广告位不支持多图轮播。 */
export function isAdvertisementCarouselDisabled(positionType?: number, position?: number): boolean {
    if (positionType === 6) return [6, 7, 8].includes(position ?? 0);
    return position === 2;
}

/** 按 Vue imgContent 校验规则检查图片、跳转值和轮播排序。 */
function validateContent(values: AdvertisementFormValues): string[] {
    const errors: string[] = [];
    values.content.forEach(item => {
        if (!item.fileId) errors.push("请上传广告图片");
        if (item.linkType === 1 && !item.advertId) errors.push("请选择广告页");
        if (item.linkType === 2 && !item.link?.trim()) errors.push("请输入链接");
        if (item.linkType === 3 && !item.activityCode?.trim()) errors.push("请选择活动");
        if (
            values.displayForm === 2 &&
            (!Number.isInteger(item.sort) || Number(item.sort) < 1 || Number(item.sort) > 5)
        ) {
            errors.push("请填写权重排序");
        }
    });
    if (values.displayForm === 2) {
        const sorts = values.content.map(item => item.sort).filter((sort): sort is number => sort != null);
        if (new Set(sorts).size !== sorts.length) errors.push("填写的权重排序不支持重复，请修改");
    }
    return errors;
}

/** PC 广告位编辑基础表单，保留 Vue 的展示形式、周期和内容数组提交结构。 */
export function AdvertisementFormPage({
    id: propId,
    loadDetail = loadAdvertisementDetail,
    update = updateAdvertisement
}: AdvertisementFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = propId ?? Number(searchParams.get("id"));
    const [values, setValues] = useState<AdvertisementFormValues>(initialValues);
    const [loading, setLoading] = useState(id > 0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [uploadedImagePaths, setUploadedImagePaths] = useState<Record<number, string>>({});
    const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
    const carouselDisabled = isAdvertisementCarouselDisabled(values.positionType, values.position);

    useEffect(() => {
        if (!(id > 0)) {
            setLoading(false);
            return;
        }
        let disposed = false;
        setLoading(true);
        void loadDetail(id)
            .then(detail => {
                if (disposed) return;
                setValues({
                    ...detail,
                    displayForm: Number(detail.displayForm) || 1,
                    timeInterval: Number(detail.timeInterval) || 1,
                    beginTime: dateOnly(detail.beginTime),
                    endTime: dateOnly(detail.endTime),
                    content: detail.content || []
                });
                setLoading(false);
            })
            .catch(() => {
                if (disposed) return;
                setErrorMessage("广告位详情加载失败");
                setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    const submit = async () => {
        if (values.displayForm !== 3 && (!values.beginTime || !values.endTime)) {
            setErrorMessage("请选择广告周期");
            return;
        }
        const contentErrors = validateContent(values);
        if (contentErrors.length) {
            setErrorMessage([...new Set(contentErrors)].join("；"));
            return;
        }
        setErrorMessage(null);
        const payload: AdvertisementFormPayload = {
            positionType: values.positionType,
            position: values.position,
            displayForm: values.displayForm,
            timeInterval: values.displayForm === 2 ? values.timeInterval : undefined,
            beginTime: values.displayForm === 3 ? undefined : buildDateTime(values.beginTime, false),
            endTime: values.displayForm === 3 ? undefined : buildDateTime(values.endTime, true),
            content: values.displayForm === 3 ? undefined : values.content
        };
        try {
            if (id > 0) await update(id, payload);
            setMessage("广告位保存成功");
            // Vue 提交成功后返回上一页，避免用户重复提交当前编辑页。
            void navigate(-1);
        } catch {
            setMessage("广告位保存失败");
        }
    };

    const addContent = () => {
        setValues(current =>
            current.content.length >= 5 ? current : { ...current, content: [...current.content, {}] }
        );
    };

    const removeContent = (index: number) => {
        setValues(current => {
            // Vue 至少保留一个图片内容模块，避免编辑页失去可提交的内容槽位。
            if (current.content.length <= 1) return current;
            return { ...current, content: current.content.filter((_, contentIndex) => contentIndex !== index) };
        });
        setUploadedImagePaths(current => {
            const next: Record<number, string> = {};
            Object.entries(current).forEach(([key, path]) => {
                const contentIndex = Number(key);
                if (contentIndex < index) next[contentIndex] = path;
                if (contentIndex > index) next[contentIndex - 1] = path;
            });
            return next;
        });
    };

    /** 对齐 Vue UploadImg：先校验图片类型和 5MB 大小，再回填上传结果。 */
    const uploadContentImage = async (index: number, file: File) => {
        const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif"];
        if (!allowedTypes.includes(file.type)) {
            setErrorMessage("仅支持 JPG/PNG/GIF/WebP/AVIF 图片");
            return;
        }
        if (file.size >= 5 * 1024 * 1024) {
            setErrorMessage("图片大小不能超过 5MB");
            return;
        }

        setErrorMessage(null);
        setUploadingIndex(index);
        try {
            const uploadedFile = await uploadAdvertisementImage(file);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setValues(current => ({
                ...current,
                content: current.content.map((content, contentIndex) =>
                    contentIndex === index ? { ...content, fileId: uploadedFile.id } : content
                )
            }));
            setUploadedImagePaths(current => ({ ...current, [index]: uploadedFile.path || "" }));
            setMessage(null);
        } catch {
            setErrorMessage("广告图片上传失败");
        } finally {
            setUploadingIndex(current => (current === index ? null : current));
        }
    };

    if (loading) return <div role="status">广告位加载中...</div>;

    return (
        <section aria-label="advertisement-form-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>{id > 0 ? "修改广告位" : "新增广告位"}</Typography.Title>
                {message ? <Alert message={message} role="alert" type="success" /> : null}
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {/* 对齐旧 Vue ad/edit.vue 的四列广告位表单布局。 */}
                <ZhGrid cols={4} gap={[20, 0]}>
                    <Space className="w-full" orientation="vertical">
                        <Typography.Text>广告位置：{values.position ?? "未设置"}</Typography.Text>
                        <fieldset>
                            <legend>展示形式</legend>
                            <label>
                                <input
                                    aria-label="展示形式-固定单图"
                                    checked={values.displayForm === 1}
                                    name="displayForm"
                                    type="radio"
                                    value="1"
                                    onChange={() =>
                                        setValues(current => ({ ...current, displayForm: 1, timeInterval: 1 }))
                                    }
                                />
                                固定单图
                            </label>
                            <label>
                                <input
                                    aria-label="展示形式-多图轮播"
                                    checked={values.displayForm === 2}
                                    disabled={carouselDisabled}
                                    name="displayForm"
                                    type="radio"
                                    value="2"
                                    onChange={() =>
                                        setValues(current => ({ ...current, displayForm: 2, timeInterval: 1 }))
                                    }
                                />
                                多图轮播
                            </label>
                            {values.positionType === 11 ? (
                                <label>
                                    <input
                                        aria-label="展示形式-不展示"
                                        checked={values.displayForm === 3}
                                        name="displayForm"
                                        type="radio"
                                        value="3"
                                        onChange={() => setValues(current => ({ ...current, displayForm: 3 }))}
                                    />
                                    不展示
                                </label>
                            ) : null}
                        </fieldset>
                        {values.displayForm === 2 ? (
                            <fieldset>
                                <legend>轮播间隔</legend>
                                <label>
                                    <input
                                        aria-label="轮播间隔-3秒"
                                        checked={values.timeInterval === 1}
                                        name="timeInterval"
                                        type="radio"
                                        value="1"
                                        onChange={() => setValues(current => ({ ...current, timeInterval: 1 }))}
                                    />
                                    3秒
                                </label>
                                <label>
                                    <input
                                        aria-label="轮播间隔-5秒"
                                        checked={values.timeInterval === 2}
                                        name="timeInterval"
                                        type="radio"
                                        value="2"
                                        onChange={() => setValues(current => ({ ...current, timeInterval: 2 }))}
                                    />
                                    5秒
                                </label>
                            </fieldset>
                        ) : null}
                        {values.displayForm !== 3 ? (
                            <>
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="至"
                                    startPlaceholder="广告周期开始"
                                    endPlaceholder="广告周期结束"
                                    value={[dateOnly(values.beginTime), dateOnly(values.endTime)]}
                                    onChange={value =>
                                        setValues(current => ({
                                            ...current,
                                            beginTime: String(value?.[0] || ""),
                                            endTime: String(value?.[1] || "")
                                        }))
                                    }
                                />
                            </>
                        ) : null}
                        {values.displayForm !== 3
                            ? values.content.map((item, index) => (
                                  <Card key={index} size="small" title={`图片内容 ${index + 1}`}>
                                      <Space className="w-full" orientation="vertical">
                                          <Input
                                              aria-label={`图片文件ID ${index + 1}`}
                                              inputMode="numeric"
                                              placeholder="请输入图片文件ID"
                                              type="number"
                                              value={item.fileId ?? ""}
                                              onChange={event =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? {
                                                                    ...content,
                                                                    fileId: Number(event.target.value) || undefined
                                                                }
                                                              : content
                                                      )
                                                  }))
                                              }
                                          />
                                          <label>
                                              上传广告图片 {index + 1}
                                              <input
                                                  accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                                                  aria-label={`上传广告图片 ${index + 1}`}
                                                  disabled={uploadingIndex === index}
                                                  type="file"
                                                  onChange={event => {
                                                      const file = event.currentTarget.files?.[0];
                                                      if (file) void uploadContentImage(index, file);
                                                      // 允许用户再次选择同一个文件触发 change。
                                                      event.currentTarget.value = "";
                                                  }}
                                              />
                                          </label>
                                          {uploadedImagePaths[index] ? (
                                              <Typography.Text>图片地址：{uploadedImagePaths[index]}</Typography.Text>
                                          ) : null}
                                          <select
                                              aria-label={`跳转类型 ${index + 1}`}
                                              value={item.linkType ?? ""}
                                              onChange={event =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? {
                                                                    ...content,
                                                                    linkType: Number(event.target.value) || undefined,
                                                                    // Vue 切换跳转类型时清空不再适用的旧值。
                                                                    advertId: undefined,
                                                                    link: undefined,
                                                                    activityCode: undefined
                                                                }
                                                              : content
                                                      )
                                                  }))
                                              }
                                          >
                                              <option value="">请选择跳转类型</option>
                                              <option value="1">广告页</option>
                                              <option value="2">链接</option>
                                              <option value="3">活动</option>
                                              <option value="4">不跳转</option>
                                          </select>
                                          {item.linkType === 1 ? (
                                              <Input
                                                  aria-label={`广告页ID ${index + 1}`}
                                                  inputMode="numeric"
                                                  placeholder="请输入广告页ID"
                                                  type="number"
                                                  value={item.advertId ?? ""}
                                                  onChange={event =>
                                                      setValues(current => ({
                                                          ...current,
                                                          content: current.content.map((content, contentIndex) =>
                                                              contentIndex === index
                                                                  ? {
                                                                        ...content,
                                                                        advertId:
                                                                            Number(event.target.value) || undefined
                                                                    }
                                                                  : content
                                                          )
                                                      }))
                                                  }
                                              />
                                          ) : null}
                                          {item.linkType === 2 ? (
                                              <Input
                                                  aria-label={`跳转链接 ${index + 1}`}
                                                  maxLength={64}
                                                  placeholder="请输入跳转链接"
                                                  value={item.link || ""}
                                                  onChange={event =>
                                                      setValues(current => ({
                                                          ...current,
                                                          content: current.content.map((content, contentIndex) =>
                                                              contentIndex === index
                                                                  ? { ...content, link: event.target.value }
                                                                  : content
                                                          )
                                                      }))
                                                  }
                                              />
                                          ) : null}
                                          {item.linkType === 3 ? (
                                              <Input
                                                  aria-label={`活动编码 ${index + 1}`}
                                                  placeholder="请输入活动编码"
                                                  value={item.activityCode || ""}
                                                  onChange={event =>
                                                      setValues(current => ({
                                                          ...current,
                                                          content: current.content.map((content, contentIndex) =>
                                                              contentIndex === index
                                                                  ? { ...content, activityCode: event.target.value }
                                                                  : content
                                                          )
                                                      }))
                                                  }
                                              />
                                          ) : null}
                                          {values.displayForm === 2 ? (
                                              <Input
                                                  aria-label={`排序 ${index + 1}`}
                                                  inputMode="numeric"
                                                  max={5}
                                                  min={1}
                                                  placeholder="请输入1~5之间数字"
                                                  type="number"
                                                  value={item.sort ?? ""}
                                                  onChange={event =>
                                                      setValues(current => ({
                                                          ...current,
                                                          content: current.content.map((content, contentIndex) =>
                                                              contentIndex === index
                                                                  ? {
                                                                        ...content,
                                                                        sort: Number(event.target.value) || undefined
                                                                    }
                                                                  : content
                                                          )
                                                      }))
                                                  }
                                              />
                                          ) : null}
                                          {values.content.length > 1 ? (
                                              <Button
                                                  aria-label={`删除图片内容 ${index + 1}`}
                                                  onClick={() => removeContent(index)}
                                              >
                                                  删除
                                              </Button>
                                          ) : null}
                                      </Space>
                                  </Card>
                              ))
                            : null}
                        {values.displayForm === 2 && values.content.length < 5 ? (
                            <Button aria-label="新增图片内容" onClick={addContent}>
                                新增
                            </Button>
                        ) : null}
                        <Typography.Text>已配置内容：{values.content.length} 条</Typography.Text>
                        <Space>
                            <Button aria-label="确定" type="primary" onClick={() => void submit()}>
                                确定
                            </Button>
                            <Button aria-label="返回" onClick={() => void navigate(-1)}>
                                返回
                            </Button>
                        </Space>
                    </Space>
                </ZhGrid>
            </Card>
        </section>
    );
}

export default AdvertisementFormPage;
